import { useCallback } from "react";
import { useReactFlow, Node, ReactFlowInstance } from "reactflow"; // Added Edge type
import { useActionSnackbar } from "../contexts/ActionSnackbarContext";
import { captureDiagram } from "../utils/diagramCapture";
import { BoundingBox } from "../utils/imageCropping"; // Import BoundingBox
import useStore from '../store/useStore'; // Import the Zustand store

/**
 * Hook for copying selected nodes to clipboard as PNG image
 * @param reactFlowInstanceFromCanvas The ReactFlow instance passed from the Canvas component
 */
export function useClipboardImage(reactFlowInstanceFromCanvas: ReactFlowInstance | null | undefined) {
  const { getNodes, getEdges, getViewport, setViewport, fitView: rfFitView } = useReactFlow();
  const { showSnackbar } = useActionSnackbar();
  const storeSetNodes = useStore(state => state.setNodes);
  const storeSetEdges = useStore(state => state.setEdges);

  const copySelectedAsImage = useCallback(async () => {
    if (!reactFlowInstanceFromCanvas) {
      console.error("ReactFlow instance not available for copySelectedAsImage");
      showSnackbar("Cannot copy image: Canvas instance not ready");
      return;
    }

    const allNodes = getNodes(); // Get all nodes once
    const allEdges = getEdges(); // Get all edges once

    const selectedFlowNodes = allNodes.filter(node => node.selected);

    if (selectedFlowNodes.length === 0) {
      showSnackbar("No nodes selected to copy");
      return;
    }

    const currentViewport = getViewport();
    let nodesToCapture: Node[] = []; // This will hold the nodes we actually want to fit the view to
    // Check if only a group node is selected - we only want to capture groups
    const isSingleGroupSelected = selectedFlowNodes.length === 1 && selectedFlowNodes[0].type === 'group';

    if (!isSingleGroupSelected) {
      showSnackbar("Please select a single group node to capture.");
      return;
    }

    const groupNode = selectedFlowNodes[0];
    const groupId = groupNode.id;
    
    // Get the children nodes of the group for potential operations
    const childNodes = allNodes.filter(node => node.parentId === groupId);
    
    if (childNodes.length === 0) {
      showSnackbar("Selected group is empty, nothing to copy.");
      return;
    }
    
    // We'll fit view to the group node itself, not its children
    nodesToCapture = [groupNode];

    // Store IDs of originally selected elements
    const originallySelectedNodeIds = selectedFlowNodes.map(n => n.id);
    const originallySelectedEdgeIds = allEdges.filter(e => e.selected).map(e => e.id);

    try {
      // 1. Temporarily deselect all nodes and edges
      storeSetNodes(allNodes.map(n => ({ ...n, selected: false })));
      storeSetEdges(allEdges.map(e => ({ ...e, selected: false })));

      // Wait for DOM updates after deselection
      await new Promise(resolve => setTimeout(resolve, 50)); 
      
      // 2. Fit view to the group node itself, not its children
      if (rfFitView) {
        rfFitView({
          nodes: nodesToCapture.map(node => ({ id: node.id })), 
          padding: 0.2, // Slightly increased padding to ensure the group is fully visible
          duration: 0,
        });
      }
      
      // Wait for DOM updates and view fitting
      await new Promise(resolve => setTimeout(resolve, 100)); // Ensure view is settled

      // 3. Calculate clipArea if a single group was selected
      let clipAreaForCapture: BoundingBox | undefined = undefined;
      if (isSingleGroupSelected) {
        const groupNode = selectedFlowNodes[0]; 
        const viewport = getViewport();

        if (groupNode && typeof groupNode.position?.x === 'number' && typeof groupNode.position?.y === 'number' && groupNode.width && groupNode.height) {
          const padding = 4; // Total 4px padding on each side

          const groupRectX = groupNode.position.x * viewport.zoom + viewport.x;
          const groupRectY = groupNode.position.y * viewport.zoom + viewport.y;
          const groupRectWidth = groupNode.width * viewport.zoom;
          const groupRectHeight = groupNode.height * viewport.zoom;

          const clipX = groupRectX - padding;
          const clipY = groupRectY - padding;
          const clipWidth = groupRectWidth + (padding * 2);
          const clipHeight = groupRectHeight + (padding * 2);

          clipAreaForCapture = {
            x: clipX,
            y: clipY,
            width: clipWidth,
            height: clipHeight,
          };
        } else {
          console.warn("Selected group node properties (position, width, height) not available for clipping.");
        }
      }

      // 4. Capture the diagram (with transparent background for clipboard)
      const imageDataUrl = await captureDiagram(clipAreaForCapture, {
        addWhiteBackground: false, // Keep transparency for clipboard
        cropEmptySpace: false // Don't auto-crop for clipboard
      });

      // 5. Copy to clipboard
      const response = await fetch(imageDataUrl);
      const blob = await response.blob();
      try {
        await navigator.clipboard.write([new ClipboardItem({ [blob.type]: blob })]);
        showSnackbar("Copied to clipboard as image");
      } catch (clipboardError) {
        console.error("Clipboard API error:", clipboardError);
        const link = document.createElement('a');
        link.href = imageDataUrl;
        link.download = 'diagram-export.png';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        showSnackbar("Couldn't copy to clipboard. Image downloaded instead.");
      }

    } catch (error)
     {
      console.error("Error copying image to clipboard:", error);
      showSnackbar("Failed to copy image to clipboard");
    } finally {
      // 6. Restore original selection state
      // It's important to get the fresh state of nodes/edges here in case other operations happened
      const currentNodes = getNodes();
      const currentEdges = getEdges();
      storeSetNodes(currentNodes.map(n => ({ ...n, selected: originallySelectedNodeIds.includes(n.id) })));
      storeSetEdges(currentEdges.map(e => ({ ...e, selected: originallySelectedEdgeIds.includes(e.id) })));
      
      // Restore viewport
      setViewport(currentViewport);
    }
  }, [
    getNodes, 
    getEdges, 
    getViewport, 
    setViewport, 
    rfFitView, 
    showSnackbar, 
    reactFlowInstanceFromCanvas,
    storeSetNodes, // Add store actions to dependencies
    storeSetEdges
  ]);

  return {
    copySelectedAsImage
  };
}
